import { useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'

import { dealFileService, type DealFieldCatalogue } from '@/services/dealFile'
import { LOAN_QUERY_KEYS } from '@/services/loanQueryKeys'

/**
 * The controlled vocabularies a reviewer picks from.
 *
 * These were typed here by hand — fourteen property types, nine loan purposes, an index list with an "N/A" the
 * record never accepted — and disagreed with the deal catalogue the record is validated against, so a reviewer
 * could not pick "Office Conversion", "Condominium" or "Other", and could pick a value the save then refused.
 * The options are the catalogue's own (`GET /loans/field-definitions`, the same query the dossier and Manual
 * Entry share), which the server builds from the one vocabulary in `app/parsing/vocabularies.py`.
 */

/** The datapoints a reviewer answers from a list: the ones the record stores as an enum. Their options are not
 *  listed here — only which datapoints have any. */
const LISTED_DATAPOINTS: ReadonlySet<string> = new Set([
  'rate_type',
  'payment_frequency',
  'lien_position',
  'index_type',
  'purpose',
  'asset_type',
  'unit_of_measure',
])

/** The options for a datapoint, from the catalogue; undefined for a datapoint with no list, or while the
 *  catalogue has not loaded (the panel then offers free entry). The extraction's name for a datapoint is
 *  mapped to the catalogue field it fills by the catalogue's own `intake_aliases` (`index_type` -> `index`). */
export function datapointOptions(
  catalogue:
    | {
        fields: ReadonlyArray<{ name: string; options: readonly string[] }>
        intake_aliases?: DealFieldCatalogue['intake_aliases']
      }
    | undefined,
  datapoint: string,
): string[] | undefined {
  if (!catalogue || !LISTED_DATAPOINTS.has(datapoint)) return undefined
  const name = catalogue.intake_aliases?.[datapoint] ?? datapoint
  const options = catalogue.fields.find((field) => field.name === name)?.options
  return options && options.length > 0 ? [...options] : undefined
}

/** `(datapoint) => options` over the shared catalogue query. */
export function useDatapointOptions(): (datapoint: string) => string[] | undefined {
  const query = useQuery({
    queryKey: LOAN_QUERY_KEYS.dealFieldCatalogue,
    queryFn: () => dealFileService.getCatalogue(),
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
  const catalogue = query.data
  return useCallback((datapoint: string) => datapointOptions(catalogue, datapoint), [catalogue])
}
